/**
 * PATH: packages/ui/src/components/sidebar/index.tsx
 * PURPOSE: Main Sidebar orchestrator — composes header, nav, hubs, footer
 * WHY: Core navigation component for all Quotech modules
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { ChevronDown, Grid3X3, Pin, Zap } from 'lucide-react'
import { cn } from '../../lib/utils'
import { isConsolidatedHub, UNIFIED_HUB_CONFIGS } from '../../config/unifiedHubConfig'
import { useContextMenu } from '../../contextMenu'
import type { ContextMenuItem } from '../../contextMenu/types'
import type { NavItem } from '../types'
import type { NavSection, SidebarProps } from './types'
import { SidebarChrome } from '../sidebar-chrome'
import { HubLinkWithFlyout } from './HubLinkWithFlyout'
import { NavItemWithFlyout } from './NavItemWithFlyout'
import { CollapsedHubsPortal } from './CollapsedHubsPortal'
import { SidebarHeader } from './SidebarHeader'
import { SidebarFooter } from './SidebarFooter'
import { HiddenNavTray } from './HiddenNavTray'
import { buildNavContextMenu } from './buildNavContextMenu'

/**
 * Rewrite an external hub URL to an internal SPA path.
 * e.g., 'https://workforce.quotech.ai/sso?token=...&returnTo=/allocation'
 *    → '/workforce/allocation'
 * Strips SSO tokens/params — they're not needed for internal navigation.
 */
function toInternalPath(href: string, hubId: string): string {
  try {
    const url = new URL(href)
    // If the URL has a returnTo param, use that as the path
    const returnTo = url.searchParams.get('returnTo')
    if (returnTo) {
      return `/${hubId}${returnTo.startsWith('/') ? returnTo : '/' + returnTo}`
    }
    // SSO entry point → use hub default path
    if (url.pathname === '/sso' || url.pathname === '/') {
      const cfg = UNIFIED_HUB_CONFIGS[hubId]
      return cfg ? `/${hubId}/${cfg.defaultPath}` : `/${hubId}`
    }
    // Regular path → just prefix with hub
    return `/${hubId}${url.pathname}`
  } catch {
    const cfg = UNIFIED_HUB_CONFIGS[hubId]
    return cfg ? `/${hubId}/${cfg.defaultPath}` : `/${hubId}`
  }
}

/**
 * Drop duplicate-id entries, keeping the first occurrence. Used so a
 * mis-authored nav config (or a pin that duplicates a main item) can
 * never produce two React children with the same `key`.
 */
function dedupeById(items: NavItem[]): NavItem[] {
  const seen = new Set<string>()
  const out: NavItem[] = []
  for (const item of items) {
    const id = item?.id
    if (id && seen.has(id)) continue
    if (id) seen.add(id)
    out.push(item)
  }
  return out
}

/** Internal hrefs for hub-route resume (must be the full rail, not one item). */
function collectInternalHrefs(items: Array<{ href?: string; external?: boolean; children?: Array<{ href?: string }> }> | undefined): string[] {
  const hrefs: string[] = []
  const push = (href?: string, external?: boolean) => {
    if (!href || external || /^https?:\/\//i.test(href)) return
    hrefs.push(href)
  }
  for (const item of items ?? []) {
    push(item.href, item.external)
    for (const child of item.children ?? []) push(child.href)
  }
  return hrefs
}

export type { SidebarProps } from './types'
export type { NavItem, ExternalLink, User, ModuleConfig, QuickAccessShortcut, BadgeVariant } from './types'

export function Sidebar({
  navItems, secondaryNavItems, externalLinks, hideExternalLinks = false,
  quickAccessItems, pinnedItems, hiddenItems, onUnhideItem,
  resolveNavContext, onNavItemClick,
  module, user, collapsed = false, onCollapsedChange,
  onOpenSearch, onLogout, onOpenCustomize, onHubClick,
  currentHubId, sectionLabels = {}, actionSlot, className,
}: SidebarProps) {
  const [hubsExpanded, setHubsExpanded] = useState(true)
  const [collapsedHubsOpen, setCollapsedHubsOpen] = useState(false)
  const [collapsedHubsPosition, setCollapsedHubsPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [collapsedHubsMenuTitle, setCollapsedHubsMenuTitle] = useState<'Switch Hub' | 'All Hubs'>('All Hubs')
  const collapsedHubsAnchorRef = useRef<'header' | 'grid' | null>(null)
  const [expandedNavItems, setExpandedNavItems] = useState<Set<string>>(new Set())
  const [activeHubFlyout, setActiveHubFlyout] = useState<string | null>(null)
  const lastSidebarScrollAtRef = useRef<number>(0)
  const navScrollRef = useRef<HTMLElement | null>(null)
  const collapsedHubHeaderButtonRef = useRef<HTMLButtonElement>(null)
  const collapsedHubsDropdownRef = useRef<HTMLDivElement>(null)

  // ─── Rewrite consolidated hub links to internal paths ─────
  // When onHubClick is provided (unified mode), rewrite external subdomain
  // URLs to internal SPA paths. This ensures <a href> points to the right
  // place even if the onClick handler fails (safety net).
  const effectiveExternalLinks = useMemo(() => {
    if (!onHubClick || !externalLinks) return externalLinks
    return externalLinks.map(link => {
      if (!link.id || !isConsolidatedHub(link.id)) return link
      const hubId = link.id
      const rewrittenHref = toInternalPath(link.href, hubId)
      const rewrittenPages = link.pages?.map(page => ({
        ...page,
        href: toInternalPath(page.href, hubId),
      }))
      return { ...link, href: rewrittenHref, pages: rewrittenPages || link.pages }
    })
  }, [onHubClick, externalLinks])

  // When `hideExternalLinks` is set the host has relocated hub
  // navigation elsewhere (e.g. the GlobalSearch pinned Hubs bar), so
  // the rail, the collapsed portal, and the header switcher all render
  // WITHOUT hubs. `externalLinks` still flows in for search-as-you-type.
  const railExternalLinks = hideExternalLinks ? undefined : effectiveExternalLinks

  // ─── Defensive de-dupe by id ─────────────────────────────
  // A nav item must never render twice WITHIN THE SAME list. This
  // happens when a hub's config accidentally lists the same id twice
  // (e.g. the workforce nav once declared `approvals` in two places):
  // React then renders two <li key="approvals"> as siblings — a key
  // collision that makes the item flicker, vanish, or appear twice
  // between re-renders. Keep the FIRST occurrence (config order is
  // intentional) and drop later collisions.
  //
  // NOTE: we DON'T cross-dedupe pinned-vs-main. Pinned items are a
  // deliberate quick-access copy at the top of the rail and live in a
  // SEPARATE <ul>, so a pinned item that's also in the main nav is not
  // a key collision and is shown in both sections by design.
  const dedupedNavItems = useMemo(() => dedupeById(navItems), [navItems])
  const dedupedSecondaryNavItems = useMemo(
    () => (secondaryNavItems ? dedupeById(secondaryNavItems) : secondaryNavItems),
    [secondaryNavItems],
  )
  const dedupedPinnedItems = useMemo(() => {
    if (!pinnedItems || pinnedItems.length === 0) return pinnedItems
    const seen = new Set<string>()
    return pinnedItems.filter((p) => {
      if (!p.id || seen.has(p.id)) return false
      seen.add(p.id)
      return true
    })
  }, [pinnedItems])

  const hubNavTargets = useMemo(
    () => [
      ...collectInternalHrefs(dedupedNavItems),
      ...collectInternalHrefs(dedupedSecondaryNavItems),
      ...collectInternalHrefs(dedupedPinnedItems),
    ],
    [dedupedNavItems, dedupedSecondaryNavItems, dedupedPinnedItems],
  )

  const accentBgClass = module.accentBgClass || 'bg-teal-600'
  const labels = {
    main: sectionLabels.main || 'Navigation',
    secondary: sectionLabels.secondary || 'Tools',
    quickAccess: sectionLabels.quickAccess || 'Quick Access',
    hubs: sectionLabels.hubs || 'Hubs',
    pinned: sectionLabels.pinned || 'Pinned',
  }

  // Shared right-click context menu — single portal at the Sidebar
  // root so we never end up with two menus open simultaneously. The
  // menu shape comes from `buildNavContextMenu`; per-item resolution
  // (isPinned, canMoveUp/Down, action callbacks) is owned by the
  // host via the optional `resolveNavContext` prop. When that prop is
  // absent the right-click handler is undefined and `NavItemWithFlyout`
  // falls through to the browser's native menu — zero behaviour
  // change for hubs that haven't opted in.
  const ctxMenu = useContextMenu()
  const [ctxItem, setCtxItem] = useState<{ item: NavItem; section: NavSection } | null>(null)

  const handleNavContextMenu = useMemo(() => {
    if (!resolveNavContext) return undefined
    return (section: NavSection) => (item: NavItem) => (e: React.MouseEvent) => {
      e.preventDefault()
      setCtxItem({ item, section })
      ctxMenu.openAt(e)
    }
  }, [resolveNavContext, ctxMenu])

  const ctxMenuItems = useMemo<ContextMenuItem[]>(() => {
    if (!ctxItem || !resolveNavContext) return []
    const resolution = resolveNavContext(ctxItem.item, ctxItem.section)
    return buildNavContextMenu(
      ctxItem.item,
      {
        isPinned: resolution.isPinned,
        isHidden: resolution.isHidden,
        canMoveUp: resolution.canMoveUp,
        canMoveDown: resolution.canMoveDown,
        shortcut: resolution.shortcut,
      },
      resolution.actions,
    )
  }, [ctxItem, resolveNavContext])

  const toggleNavItemExpanded = useCallback((itemId: string) => {
    setExpandedNavItems(prev => {
      const next = new Set(prev)
      next.has(itemId) ? next.delete(itemId) : next.add(itemId)
      return next
    })
  }, [])

  const toggleCollapsedHubsMenu = useCallback(
    (anchor: 'header' | 'grid') => (event: React.MouseEvent<HTMLButtonElement>) => {
      if (collapsedHubsOpen && collapsedHubsAnchorRef.current === anchor) {
        setCollapsedHubsOpen(false)
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      setCollapsedHubsPosition({ top: rect.top, left: rect.right + 8 })
      setCollapsedHubsMenuTitle(anchor === 'header' ? 'Switch Hub' : 'All Hubs')
      collapsedHubsAnchorRef.current = anchor
      setCollapsedHubsOpen(true)
    },
    [collapsedHubsOpen]
  )

  // Close collapsed hubs portal on outside click. The portal is now
  // only invoked from the SidebarHeader's "Switch Hub" anchor (the
  // grid-button anchor was removed when the collapsed Hubs section
  // became a real scrollable icon list — FEAT-2026-05-22-…); but
  // the header anchor still uses this guard.
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      const clickedTrigger = collapsedHubHeaderButtonRef.current?.contains(event.target as Node)
      const clickedDropdown = collapsedHubsDropdownRef.current?.contains(event.target as Node)
      if (!clickedTrigger && !clickedDropdown) setCollapsedHubsOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Close flyouts during scroll
  useEffect(() => {
    const el = navScrollRef.current
    if (!el) return
    const mark = () => { lastSidebarScrollAtRef.current = Date.now(); setActiveHubFlyout(null) }
    el.addEventListener('scroll', mark, { passive: true })
    el.addEventListener('wheel', mark, { passive: true })
    el.addEventListener('touchmove', mark, { passive: true })
    return () => { el.removeEventListener('scroll', mark); el.removeEventListener('wheel', mark); el.removeEventListener('touchmove', mark) }
  }, [])

  // ───────────────────────────────────────────────────────────────
  // Render slots for SidebarChrome.
  //
  // The structural shell (outer <aside>, position/width/background,
  // Cmd+B handler, optional mobile drawer, optional ContextMenu
  // portal) lives in `SidebarChrome`. This wrapper supplies the
  // master-specific content for each slot.
  //
  // SidebarChrome props deviating from tenant defaults:
  //   - position='relative'            (master lives inside DashboardLayout's
  //                                     layout slot; the floating collapse
  //                                     chevron in SidebarFooter is positioned
  //                                     absolutely against this aside.)
  //   - expandedWidthClass='w-60'      (240px — slightly wider than tenant's
  //                                     224px to fit the search bar + hub
  //                                     switcher header.)
  //   - backgroundClass='bg-card'      (hub apps theme through the `card`
  //                                     token, not `sidebar`.)
  // ───────────────────────────────────────────────────────────────

  const topPortalSlot = collapsed && railExternalLinks ? (
    <CollapsedHubsPortal
      open={collapsedHubsOpen}
      position={collapsedHubsPosition}
      title={collapsedHubsMenuTitle}
      externalLinks={railExternalLinks}
      dropdownRef={collapsedHubsDropdownRef}
      onClose={() => setCollapsedHubsOpen(false)}
      onHubClick={onHubClick}
    />
  ) : null

  const headerSlot = (
    <>
      <SidebarHeader
        module={module}
        collapsed={collapsed}
        externalLinks={railExternalLinks}
        onOpenSearch={onOpenSearch}
        onOpenCustomize={onOpenCustomize}
        onHubClick={onHubClick}
        collapsedHubHeaderButtonRef={collapsedHubHeaderButtonRef}
        onToggleCollapsedHubs={toggleCollapsedHubsMenu}
      />
      {actionSlot && <div className="px-2 pb-1">{actionSlot}</div>}
    </>
  )

  const navSlot = (
    <nav
      id="quotech-primary-nav"
      ref={navScrollRef}
      role="navigation"
      aria-label="Primary"
      className={cn(
        'flex-1 min-h-0 py-1',
        /* Collapsed nav uses a rounded dock card wrapper — same visual
           language as the tenant MobileDock at the bottom of the
           viewport. Expanded nav is a plain scrollable column. */
        collapsed
          ? 'mx-1 flex flex-col overflow-hidden rounded-2xl border border-border/80 bg-card/95 shadow-md shadow-slate-900/5'
          : 'overflow-y-auto px-2 scrollbar-rail-thin',
      )}
    >
      {/* Thin visible scrollbar (not hidden). `.scrollbar-rail-thin` is
          defined in HQ src/index.css AND packages/ui base.css — HQ does
          not import base.css, so the HQ copy is what makes this work. */}
      <div
        className={cn(
          collapsed &&
            'flex-1 overflow-y-auto overflow-x-hidden px-1 py-1.5 scrollbar-rail-thin',
        )}
      >

        {/* Pinned Section — renders ABOVE main nav when the host
            supplies pinnedItems. Empty/undefined → section hidden
            entirely (no border-bottom, no header), so non-opted-in
            hubs see zero visual diff. */}
        {dedupedPinnedItems && dedupedPinnedItems.length > 0 && (
          <div className="mb-2 pb-2 border-b border-border/60">
            {!collapsed && (
              <p className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                <Pin className="w-3 h-3" />{labels.pinned}
              </p>
            )}
            <ul className="space-y-0.5">
              {dedupedPinnedItems
                // PinnedNavItem → NavItem adapter. PinnedNavItem.href is
                // always an absolute string (mirrors the FE→BE contract);
                // we trust `NavItemWithFlyout`'s own `isAbsoluteHref`
                // check to route same-origin URLs back to the SPA.
                .map((pin) => ({
                  id: pin.id,
                  label: pin.label,
                  icon: pin.icon,
                  href: pin.href,
                } as NavItem))
                .map((item) => (
                  <li key={item.id}>
                    <NavItemWithFlyout
                      item={item}
                      collapsed={collapsed}
                      accentBgClass={accentBgClass}
                      hubNavTargets={hubNavTargets}
                      isExpanded={false}
                      onContextMenu={handleNavContextMenu?.('pinned')(item)}
                    />
                  </li>
                ))}
            </ul>
          </div>
        )}

        {!collapsed && dedupedNavItems.length > 0 && (
          <p className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{labels.main}</p>
        )}
        <ul className="space-y-0.5">
          {dedupedNavItems.map((item) => (
            <li key={item.id}>
              <NavItemWithFlyout item={item} collapsed={collapsed} accentBgClass={accentBgClass}
                hubNavTargets={hubNavTargets}
                isExpanded={expandedNavItems.has(item.id)} onToggleExpand={() => toggleNavItemExpanded(item.id)}
                onContextMenu={handleNavContextMenu?.('main')(item)}
                onActivate={onNavItemClick ? () => onNavItemClick(item) : undefined} />
            </li>
          ))}
        </ul>

        {/* Secondary Navigation */}
        {dedupedSecondaryNavItems && dedupedSecondaryNavItems.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            {!collapsed && <p className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{labels.secondary}</p>}
            <ul className="space-y-0.5">
              {dedupedSecondaryNavItems.map((item) => (
                <li key={item.id}>
                  <NavItemWithFlyout item={item} collapsed={collapsed} accentBgClass={accentBgClass}
                    hubNavTargets={hubNavTargets}
                    isExpanded={expandedNavItems.has(item.id)} onToggleExpand={() => toggleNavItemExpanded(item.id)}
                    onContextMenu={handleNavContextMenu?.('secondary')(item)}
                    onActivate={onNavItemClick ? () => onNavItemClick(item) : undefined} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Hubs Section
            -------------
            FEAT-2026-05-22-COLLAPSED-SIDEBAR-SCROLLABLE-HUBS:
            In the EXPANDED sidebar the hubs already render as a real
            scrollable `<ul>` (the operator can see every hub at a
            glance and scroll within the nav rail). In the COLLAPSED
            sidebar the same section used to collapse down to a SINGLE
            `Grid3X3` icon that opened a portal dropdown — the operator
            had to click into the dropdown to even SEE the other hubs.
            That was the user-reported failure mode ("I should just
            scroll down not just a dropdown").
            Both branches below now render the FULL list. Expanded
            branch keeps its rich icon-+-label-+-DEV/SOON-badge
            treatment; collapsed branch renders each hub as a stacked
            icon via `HubLinkWithFlyout` in `collapsed={true}` mode —
            same hover-flyout that already exists. The grid-anchor
            ref handle is gone in this revision; the CollapsedHubsPortal
            machinery still lives for the SidebarHeader's quick-switch
            anchor (top-of-sidebar hub icon → "Switch Hub" portal).  */}
        {railExternalLinks && railExternalLinks.length > 0 && (
          <div className="mt-3 pt-3 border-t border-border">
            {!collapsed ? (
              <>
                <button
                  type="button"
                  onClick={() => setHubsExpanded(!hubsExpanded)}
                  aria-expanded={hubsExpanded}
                  aria-controls="quotech-hubs-list"
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 rounded-sm">
                  <Grid3X3 className="w-3 h-3" aria-hidden="true" />
                  <span className="flex-1 text-left">{labels.hubs}</span>
                  <ChevronDown className={cn('w-3 h-3 transition-transform', hubsExpanded && 'rotate-180')} aria-hidden="true" />
                </button>
                {hubsExpanded && (
                  <ul id="quotech-hubs-list" className="mt-1 space-y-0.5 pl-1">
                    {railExternalLinks.map((link) => (
                      <li key={link.id || link.href}>
                        <HubLinkWithFlyout link={link} collapsed={false} activeHubId={activeHubFlyout}
                          setActiveHubId={setActiveHubFlyout} lastSidebarScrollAtRef={lastSidebarScrollAtRef} onHubClick={onHubClick} currentHubId={currentHubId} />
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <ul
                id="quotech-hubs-list-collapsed"
                aria-label={labels.hubs}
                className="space-y-1 px-1"
              >
                {railExternalLinks.map((link) => (
                  <li key={link.id || link.href}>
                    <HubLinkWithFlyout
                      link={link}
                      collapsed={true}
                      activeHubId={activeHubFlyout}
                      setActiveHubId={setActiveHubFlyout}
                      lastSidebarScrollAtRef={lastSidebarScrollAtRef}
                      onHubClick={onHubClick}
                      currentHubId={currentHubId}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Quick Access */}
        {quickAccessItems && quickAccessItems.length > 0 && !collapsed && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="px-2 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1">
              <Zap className="w-3 h-3" />{labels.quickAccess}
            </p>
            <ul className="space-y-0.5">
              {quickAccessItems.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <button onClick={() => { if (item.action) { item.action(); return } if (item.href) window.location.href = item.href }}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors text-left">
                    <item.icon className="w-3.5 h-3.5 flex-shrink-0" /><span className="truncate flex-1">{item.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Hidden (N) restore tray — only renders when the host wired
            hiddenItems + onUnhideItem AND something is actually hidden.
            Lives at the foot of the nav so it never competes with the
            primary items the operator kept. */}
        {hiddenItems && hiddenItems.length > 0 && onUnhideItem && (
          <HiddenNavTray
            hiddenItems={hiddenItems}
            onUnhide={onUnhideItem}
            collapsed={collapsed}
          />
        )}
        </div>
      </nav>
  )

  const footerSlot = (
    <SidebarFooter
      collapsed={collapsed}
      user={user}
      onLogout={onLogout}
      onCollapsedChange={onCollapsedChange}
    />
  )

  return (
    <SidebarChrome
      collapsed={collapsed}
      onToggleCollapsed={
        onCollapsedChange ? () => onCollapsedChange(!collapsed) : undefined
      }
      position="relative"
      expandedWidthClass="w-60"
      surfaceClass="bg-card border-r border-border"
      className={className}
      topPortal={topPortalSlot}
      header={headerSlot}
      nav={navSlot}
      footer={footerSlot}
      // Right-click context menu portal. Rendered ONLY when the host
      // has wired `resolveNavContext` (handleNavContextMenu becomes
      // defined). The ContextMenu itself returns null on `open=false`,
      // so this adds no DOM cost when the menu is closed.
      contextMenu={
        resolveNavContext
          ? {
              open: ctxMenu.isOpen,
              position: ctxMenu.position,
              items: ctxMenuItems,
              onClose: ctxMenu.close,
            }
          : undefined
      }
    />
  )
}

export default Sidebar
